import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Table,
  Typography,
  Upload,
} from "antd";
import {
  AMENDMENT_LIEN_COST_ADD_ENDPOINT,
  AMENDMENT_LIEN_COST_EDIT_ENDPOINT,
  EXIM_ACCOUNT_ADD_ENDPOINT,
  EXIM_ACCOUNT_LIST_ENDPOINT,
  SALES_CONTRACT_COMMERCIAL_LIST,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { commaSeparateNumber } from "utils/functions";

const { Text } = Typography;

// Constants
const DEFAULT_HEADER_ID = 2;
const DEFAULT_CURRENCY = "BDT";

/**
 * Main Component
 */
const AmendmentLienCostForm = forwardRef((props, ref) => {
  const { form, isEdit, editInfo, viewMode } = props;
  const costWatcher = Form.useWatch("cost", form);
  const navigate = useNavigate();

  // State management
  const [options, setOptions] = useState({
    salesContracts: [],
    accounts: [],
    amendments: [],
  });
  const [fileList, setFileList] = useState([]);
  const [accountInputValues, setAccountInputValues] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState({});
  const selectRefs = useRef({});

  // handle file upload change
  const handleFileChange = useCallback(({ fileList: newFileList }) => {
    setFileList(newFileList);
  }, []);

  const resetFiles = useCallback(() => {
    setFileList([]);
  }, []);

  const addFile = useCallback((file) => {
    setFileList((prev) => [...prev, file]);
  }, []);

  // Get Api service
  const getSalesContractNumber = async (filter) => {
    const body = {
      page: 1,
      per_page: 10,
      ...(filter?.reference_no && { reference_no: filter.reference_no }),
    };
    const response = await getData(
      `${SALES_CONTRACT_COMMERCIAL_LIST}`,
      null,
      body,
    );
    if (response?.status === 200) {
      setOptions((prev) => ({
        ...prev,
        salesContracts: response?.data?.data?.data || [],
      }));
    }
  };

  const getEximAccount = async () => {
    const response = await getData(EXIM_ACCOUNT_LIST_ENDPOINT, null, {
      per_page: 100000,
    });
    if (response?.status === 200) {
      const accounts = response?.data?.data?.data || [];
      setOptions((prev) => ({
        ...prev,
        accounts: accounts,
      }));
    }
  };

  const getCharges = useCallback(async () => {
    const response = await getData(
      `/api/exim-cost/getTypebyHead/?cost_head_id=${DEFAULT_HEADER_ID}`,
    );
    if (response?.status === 200) {
      const costFields = response.data.map((charge, index) => ({
        key: charge.id,
        type: charge.type.name,
        exim_charge_id: charge.id,
        exim_charge_amount: charge.amount,
        acc_no: null,
        currency: DEFAULT_CURRENCY,
        cost_type_id: charge.cost_type_id,
      }));
      form.setFieldsValue({ cost: costFields });
    }
  }, [form]);

  // Process amendments from sales contract data
  const processAmendments = useCallback((contractData) => {
    const amendments = [];

    // Use sale_contract_amendment_info instead of parsing comma-separated values
    if (
      contractData.sale_contract_amendment_info &&
      contractData.sale_contract_amendment_info.length > 0
    ) {
      contractData.sale_contract_amendment_info.forEach((amendment) => {
        if (amendment.name === "Initial") {
          amendments.push({
            id: amendment.id,
            name: amendment.name,
            value: parseFloat(amendment.total_style_order_value),
            quantity: parseInt(amendment.total_style_order_quantity),
            date: amendment.contract_date,
            amendment_value: parseFloat(amendment.total_style_order_value), // Initial amendment has no change value
            label: amendment.name,
          });
        } else {
          amendments.push({
            id: amendment.id,
            name: amendment.name,
            value: parseFloat(amendment.last_changed_value),
            quantity: parseInt(amendment.total_style_order_quantity),
            date: amendment.contract_date,
            amendment_value: parseFloat(amendment.last_changed_value),
            label: amendment.name,
          });
        }
      });
    }

    return amendments;
  }, []);

  const fetchInitialData = useCallback(async () => {
    await getEximAccount();
    if (!isEdit) {
      await getSalesContractNumber();
      await getCharges();
      resetFiles();
    }
  }, [isEdit, resetFiles, getCharges]);

  // Initial get data on component mount
  useEffect(() => {
    fetchInitialData(isEdit);
  }, [isEdit, fetchInitialData]);

  // Separate effect to handle editInfo changes
  useEffect(() => {
    if (isEdit && editInfo) {
      // Map existing costs to form fields
      const existingCosts = editInfo.lien_charges || [];
      const costFields = existingCosts.map((charge, index) => ({
        key: charge.charges.id,
        type: charge.charges.type.name,
        exim_charge_id: charge.charges.id,
        exim_charge_amount: charge.exim_charge_amount,
        acc_no: charge.acc_no,
        currency: DEFAULT_CURRENCY,
        cost_type_id: charge.charges.cost_type_id,
      }));
      getSalesContractNumber({
        reference_no:
          editInfo?.sales_contract?.sale_contract_info?.reference_no,
      });

      // Process amendments for edit mode
      const amendments = processAmendments(editInfo?.sales_contract || {});
      setOptions((prev) => ({
        ...prev,
        amendments: amendments,
      }));

      // Calculate salesContractValue based on amendment in edit mode
      let calculatedSalesContractValue =
        editInfo?.sales_contract?.sale_contract_info?.sales_contract_value || 0;

      if (editInfo?.amendment_id && amendments.length > 0) {
        const selectedAmendmentIndex = amendments.findIndex(
          (amendment) => amendment.id === editInfo.amendment_id,
        );

        if (selectedAmendmentIndex !== -1) {
          const totalAmendments = amendments
            .filter((amendment, i) => selectedAmendmentIndex > i)
            .reduce((sum, amendment) => sum + amendment.amendment_value, 0);

          calculatedSalesContractValue = parseFloat(totalAmendments).toFixed(2);
        }
      }

      form.setFieldsValue({
        sales_contract_id: editInfo?.sales_contract_id,
        salesContractValue: calculatedSalesContractValue,
        date: editInfo?.sales_contract?.sale_contract_info?.contract_date
          ? moment(editInfo?.sales_contract?.sale_contract_info?.contract_date)
          : null,
        buyer: editInfo?.sales_contract?.buyer_name,
        tag: editInfo?.sales_contract?.tag_number,
        trn_date: editInfo?.trn_date ? moment(editInfo?.trn_date) : null,
        cost: costFields,
        // Set amendment-related fields if available in editInfo
        amendment_id: editInfo?.amendment_id || undefined,
        amendment_value: editInfo?.amendment_value || 0,
        with_amendment_total: editInfo?.with_amendment_total || 0,
      });

      // Handle voucher file
      if (editInfo?.voucher) {
        // Clear existing files first to avoid duplication
        setFileList([]);
        addFile({
          uid: "-1",
          name: editInfo?.voucher?.split("/").pop(),
          status: "done",
          url: process.env.REACT_APP_BASE_URL + "/" + editInfo.voucher,
        });
      }
    }
  }, [editInfo, isEdit, form, addFile, processAmendments]);

  // on search sales contracts
  const onSalesContractSearch = useCallback(async (value) => {
    if (value && value.length >= 2) {
      await getSalesContractNumber({ reference_no: value });
    } else if (!value) {
      await getSalesContractNumber();
    }
  }, []);

  // on select sales contract number
  const onSelectSalesContract = useCallback(
    async (value) => {
      try {
        const selectedContract = options.salesContracts.find(
          (contract) => contract.sale_contract_info.id === value,
        );

        console.log("Selected Contract:", selectedContract);

        if (selectedContract) {
          const contractInfo = selectedContract.sale_contract_info;

          // Process amendments from the contract data
          const amendments = processAmendments(selectedContract);
          setOptions((prev) => ({
            ...prev,
            amendments: amendments,
          }));

          // Set basic form values
          form.setFieldsValue({
            salesContractValue: contractInfo?.contract_value,
            date: contractInfo?.contract_date
              ? moment(contractInfo.contract_date)
              : null,
            buyer: selectedContract.buyer_name,
            tag: selectedContract.tag_number,
            amendment_id: undefined, // Clear previous amendment selection
            amendment_value: 0,
            with_amendment_total: 0,
          });
        }
      } catch (error) {
        console.error("Error handling contract change:", error);
      }
    },
    [options.salesContracts, form, processAmendments],
  );

  // Handle amendment selection
  const onSelectAmendment = useCallback(
    (amendmentId) => {
      const selectedAmendment = options.amendments.find(
        (amendment) => amendment.id === amendmentId,
      );
      const selectedAmendmentIndex = options.amendments.findIndex(
        (amendment) => amendment.id === amendmentId,
      );
      // Update amendment value and total based on selected amendment

      if (selectedAmendmentIndex === -1) return;
      const totalAmendments = options.amendments
        .filter((amendment, i) => selectedAmendmentIndex > i)
        .reduce((sum, amendment) => sum + amendment.amendment_value, 0);

      if (selectedAmendment) {
        // Update form values
        form.setFieldsValue({
          salesContractValue: parseFloat(totalAmendments).toFixed(2),
          amendment_value: selectedAmendment.amendment_value,
          with_amendment_total: parseFloat(
            totalAmendments + selectedAmendment.amendment_value,
          ).toFixed(2),
        });
      }
    },
    [options.amendments, form],
  );

  const handleCreateAccount = useCallback(
    async (fieldName) => {
      const accountNumber = accountInputValues?.trim();
      if (!accountNumber) return;

      const costs = form.getFieldValue("cost") || [];
      const findCost = costs.find((c, index) => index === fieldName);

      if (!findCost) {
        notification.error({
          message: "Error",
          description: "Cost type not found for the selected row",
          placement: "topRight",
        });
        return;
      }

      try {
        const response = await postData(EXIM_ACCOUNT_ADD_ENDPOINT, {
          account_no: accountNumber,
          cost_head_id: DEFAULT_HEADER_ID,
          cost_type_id: findCost?.cost_type_id,
        });

        if (response?.code === 200) {
          const newAccount = {
            account_no: accountNumber,
            id: response?.data?.id,
            cost_type_id: findCost?.cost_type_id,
          };

          // Add the new account to the options
          setOptions((prev) => ({
            ...prev,
            accounts: [...prev.accounts, newAccount],
          }));

          // Set the newly created account as selected
          form.setFieldValue(["cost", fieldName, "acc_no"], newAccount.id);

          // Clear the input field
          setAccountInputValues("");

          // Close dropdown after selecting new account
          setDropdownOpen((prev) => ({
            ...prev,
            [fieldName]: false,
          }));

          notification.success({
            message: "Success",
            description: response?.message || "Account created successfully",
            placement: "topRight",
          });
        }
      } catch (error) {
        console.error("Error creating exim account:", error);
        notification.error({
          message: "Error",
          description: "Failed to create account",
          placement: "topRight",
        });
      }
    },
    [form, accountInputValues],
  );

  // Form submission
  const handleSubmit = useCallback(
    async (values) => {
      try {
        const costs =
          values.cost?.map((cost) => ({
            exim_charge_id: cost.exim_charge_id,
            exim_charge_amount: cost.exim_charge_amount,
            acc_no: cost.acc_no,
          })) || [];

        // Find selected amendment data
        const selectedAmendment = options.amendments.find(
          (amendment) => amendment.id === values.amendment_id,
        );

        const formData = new FormData();
        formData.append("sales_contract_id", values.sales_contract_id);
        formData.append("sales_contract_value", values.salesContractValue);
        formData.append("with_amendment_total", values.with_amendment_total);
        formData.append("cost", JSON.stringify(costs));

        // Add amendment-specific data
        if (selectedAmendment) {
          formData.append("amendment_id", selectedAmendment.id);
          formData.append("amendment_value", values.amendment_value || 0);
        }

        if (values.trn_date) {
          formData.append("trn_date", values.trn_date.format("YYYY-MM-DD"));
        }

        if (total) {
          formData.append("total", total);
        }
        if (fileList.length > 0 && fileList[0].originFileObj) {
          formData.append("voucher", fileList[0].originFileObj);
        }
        const query = isEdit
          ? `${AMENDMENT_LIEN_COST_EDIT_ENDPOINT}/${editInfo?.id}`
          : AMENDMENT_LIEN_COST_ADD_ENDPOINT;

        const response = await postData(query, formData);

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description:
              response?.message ||
              `Amendment Lien Cost ${
                isEdit ? "updated" : "created"
              } successfully`,
            placement: "topRight",
          });
          navigate("/import-export-expense-tracking/amendment-lien-cost");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    },
    [fileList, isEdit, editInfo, navigate, options.amendments],
  );

  // Expose form instance
  useImperativeHandle(ref, () => ({ form }), [form]);

  // Render helper for account dropdown
  const renderAccountDropdown = useCallback(
    (fieldName) => (
      <Select
        ref={(ref) => {
          selectRefs.current[fieldName] = ref;
        }}
        style={{ width: "100%" }}
        placeholder="Select account number"
        disabled={viewMode}
        showSearch
        allowClear
        open={dropdownOpen[fieldName]}
        onDropdownVisibleChange={(open) => {
          setDropdownOpen((prev) => ({
            ...prev,
            [fieldName]: open,
          }));
        }}
        onSelect={() => {
          // Auto close dropdown when value is selected
          setDropdownOpen((prev) => ({
            ...prev,
            [fieldName]: false,
          }));
        }}
        options={options.accounts
          .filter((account) => {
            const findCost = costWatcher?.find(
              (cost, index) => index === fieldName,
            );
            return findCost
              ? account.cost_type_id === findCost.cost_type_id
              : false;
          })
          .map((acc) => ({
            label: acc.account_no,
            value: acc.id,
          }))}
        dropdownRender={(menu) => (
          <div style={{ width: "100%" }}>
            {menu}
            <Divider style={{ margin: "8px 0" }} />
            <Row style={{ padding: "0 8px 4px" }}>
              <Col span={16}>
                <Input
                  placeholder="Enter new account"
                  id={`new-account-${fieldName}`}
                  value={accountInputValues || ""}
                  onChange={(e) => setAccountInputValues(e.target.value)}
                />
              </Col>
              <Col span={8} style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleCreateAccount(fieldName)}
                  disabled={!accountInputValues?.trim()}
                >
                  Add Account
                </Button>
              </Col>
            </Row>
          </div>
        )}
      />
    ),
    [
      options.accounts,
      costWatcher,
      viewMode,
      handleCreateAccount,
      accountInputValues,
      setAccountInputValues,
      selectRefs,
      dropdownOpen,
      setDropdownOpen,
    ],
  );

  // Table columns configuration
  const tableColumns = useMemo(
    () => [
      {
        title: "Description",
        dataIndex: "fieldName",
        key: "id",
        width: "20%",
        render: (fieldName) => (
          <>
            <Form.Item
              name={[fieldName, "type"]}
              style={{ width: "100%" }}
              rules={[
                { required: true, message: "Please select a Description" },
              ]}
            >
              <Input disabled={true} />
            </Form.Item>
          </>
        ),
      },
      {
        title: "Amount",
        dataIndex: "fieldName",
        key: "exim_charge_amount",
        width: "25%",
        render: (fieldName) => (
          <Form.Item name={[fieldName, "exim_charge_amount"]} noStyle>
            <InputNumber
              placeholder="Enter amount"
              style={{ width: "100%" }}
              disabled={true}
            />
          </Form.Item>
        ),
      },
      {
        title: "A/C No",
        dataIndex: "fieldName",
        key: "acc_no",
        width: "25%",
        render: (fieldName) => (
          <Form.Item name={[fieldName, "acc_no"]} style={{ width: "100%" }}>
            {renderAccountDropdown(fieldName)}
          </Form.Item>
        ),
      },
      {
        title: "Currency",
        dataIndex: "fieldName",
        key: "currency",
        width: "15%",
        render: (fieldName) => (
          <Form.Item
            name={[fieldName, "currency"]}
            noStyle
            initialValue={DEFAULT_CURRENCY}
          >
            <Input disabled={true} />
          </Form.Item>
        ),
      },
    ],
    [renderAccountDropdown],
  );

  const total = costWatcher?.reduce((sum, cost) => {
    const amount = parseFloat(cost.exim_charge_amount) || 0;
    return sum + amount;
  }, 0);

  console.log("amendment lien cost form render", options.amendments);

  return (
    <div className="containt-body">
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={true}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Sales Contract Number"
                name="sales_contract_id"
                rules={[
                  {
                    required: true,
                    message: "Please select a sales contract number",
                  },
                ]}
              >
                {/* Show dropdown in both edit and create modes */}
                <Select
                  placeholder="Select sales contract"
                  onSelect={onSelectSalesContract}
                  disabled={
                    viewMode
                  } /* Disable in edit mode but still show dropdown */
                  showSearch
                  filterOption={false}
                  onSearch={onSalesContractSearch}
                  options={options.salesContracts.map((contract) => ({
                    label: contract.sale_contract_info?.reference_no,
                    value: contract.sale_contract_info.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Sales Contract Value" name="salesContractValue">
                <Input disabled={true} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Date" name="date">
                <DatePicker style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Select Amendment"
                name="amendment_id"
                rules={[
                  {
                    required: true,
                    message: "Please select an amendment",
                  },
                ]}
              >
                <Select
                  placeholder="Select Amendment"
                  onSelect={onSelectAmendment}
                  disabled={
                    viewMode ||
                    options.amendments.filter(
                      (amendment) => amendment.name !== "Initial",
                    ).length === 0
                  }
                  options={options.amendments
                    .filter((amendment) => amendment.name !== "Initial")
                    .map((amendment) => ({
                      label: amendment.name,
                      value: amendment.id,
                    }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Increase/Decrease Value" name="amendment_value">
                <InputNumber
                  style={{ width: "100%" }}
                  disabled={true}
                  precision={2}
                  step={0.01}
                  formatter={(value) => {
                    if (value === null || value === undefined) return "";
                    return `${Number(value).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  }}
                  parser={(value) => {
                    if (!value) return "";
                    return value.replace(/[^\d.-]/g, "");
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Total"
                name={"with_amendment_total"}
                initialValue={0}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled={true}
                  formatter={(value) =>
                    value !== undefined && value !== null
                      ? `${commaSeparateNumber(value)}`
                      : ""
                  }
                  precision={2}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Buyer" name="buyer">
                <Input disabled={true} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Tag" name="tag">
                <Input disabled={true} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="TRN Date" name="trn_date">
                <DatePicker
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  placeholder="Select TRN Date"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Voucher"
                name="voucher"
                rules={[
                  {
                    required: !isEdit,
                    message: "Please upload a voucher file",
                  },
                ]}
              >
                <Upload
                  fileList={fileList}
                  onChange={handleFileChange}
                  beforeUpload={() => false}
                  maxCount={1}
                  disabled={viewMode}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" // Accept common document types
                >
                  <Button icon={<UploadOutlined />} disabled={viewMode}>
                    {isEdit && fileList.length > 0
                      ? "Change File"
                      : "Select File"}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Cost Details</Divider>

          <Form.List name="cost">
            {(fields) => (
              <Table
                bordered
                dataSource={fields.map((field) => ({
                  ...field,
                  key: field.key,
                  fieldName: field.name,
                }))}
                pagination={false}
                columns={tableColumns}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={1}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} colSpan={4}>
                        <Text strong>
                          {parseFloat(total).toFixed(2)} {DEFAULT_CURRENCY}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            )}
          </Form.List>
        </Form>
      </Card>
    </div>
  );
});

AmendmentLienCostForm.displayName = "AmendmentLienCostForm";

export default AmendmentLienCostForm;
